import { useEffect, useId, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useCredentials, useGroups, useLocationTree, usePersons, useUnknownCredentials } from '../api/hooks'
import { api } from '../api/client'
import type { Person } from '../api/types'
import { useAuthStore } from '../stores/auth'
import { initials } from '../lib/format'
import { locationPaths } from '../lib/locations'
import { useCanOperate } from './PersonDuplicates'

export function fmtDate(iso: string | null): string | null {
  return iso ? iso.slice(0, 10) : null
}

/** "2026-01-01 → 2026-12-31" style validity window, null when unbounded. */
export function validityWindow(from: string | null, to: string | null): string | null {
  const f = fmtDate(from)
  const t = fmtDate(to)
  if (!f && !t) return null
  return `${f ?? '…'} → ${t ?? 'no expiry'}`
}

export function personStatusTone(status: string): 'ok' | 'muted' {
  return status === 'active' ? 'ok' : 'muted'
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

export interface PersonFormModalProps {
  open: boolean
  onClose: () => void
  /** When set, edits this person; otherwise creates a new one. */
  person?: Person
}

/** Create/edit person form — shared by the directory list and the person detail page. */
export function PersonFormModal({ open, onClose, person }: PersonFormModalProps) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [note, setNote] = useState('')
  const [emails, setEmails] = useState('')
  const [phones, setPhones] = useState('')
  const [status, setStatus] = useState('active')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [showOnIntercom, setShowOnIntercom] = useState(true)
  const [mailboxAccess, setMailboxAccess] = useState(true)

  useEffect(() => {
    if (!open) return
    setFirstName(person?.first_name ?? '')
    setLastName(person?.last_name ?? '')
    setExternalRef(person?.external_ref ?? '')
    setNote(person?.note ?? '')
    setEmails((person?.emails ?? []).join(', '))
    setPhones((person?.phones ?? []).join(', '))
    setStatus(person?.status ?? 'active')
    setValidFrom(fmtDate(person?.valid_from ?? null) ?? '')
    setValidTo(fmtDate(person?.valid_to ?? null) ?? '')
    setShowOnIntercom(person?.show_on_intercom ?? true)
    setMailboxAccess(person?.mailbox_access ?? true)
  }, [open, person])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        external_ref: externalRef.trim(),
        // PUT /persons is a full replace — always send the note (and the
        // flags below) so an edit never resets them server-side.
        note: note.trim(),
        emails: splitList(emails),
        phones: splitList(phones),
        status,
        // PUT /persons is a full replace — resend the current groups so an
        // edit does not detach the person from their groups, and always send
        // both flags (they default TRUE server-side, so omitting one would
        // silently reset it on every edit).
        groups: person?.groups ?? [],
        valid_from: validFrom || null,
        valid_to: validTo || null,
        show_on_intercom: showOnIntercom,
        mailbox_access: mailboxAccess,
      }
      return person ? api.updatePerson(person.unique_id, body) : api.createPerson(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onClose()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }

  return (
    <Modal open={open} title={person ? 'Edit person' : 'Add person'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Input
          label="External ID"
          placeholder="Third-party system reference (ERP, property management)"
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="person-note"
            className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted"
          >
            Note
          </label>
          <textarea
            id="person-note"
            rows={2}
            placeholder="Free text — kept on the person, shown on their page"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink placeholder:text-ostrel-ink-muted focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          />
        </div>
        <Input
          label="Emails"
          placeholder="comma, separated"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
        />
        <Input
          label="Phones"
          placeholder="comma, separated"
          value={phones}
          onChange={(e) => setPhones(e.target.value)}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Valid from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          <Input label="Valid to" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-xs text-ostrel-ink">
            <input
              type="checkbox"
              checked={showOnIntercom}
              onChange={(e) => setShowOnIntercom(e.target.checked)}
              className="accent-ostrel-gold"
            />
            Show on intercom
          </label>
          <p className="ml-5 text-[11px] text-ostrel-ink-muted">the door phone lists this person</p>
          <label className="flex items-center gap-2 text-xs text-ostrel-ink">
            <input
              type="checkbox"
              checked={mailboxAccess}
              onChange={(e) => setMailboxAccess(e.target.checked)}
              className="accent-ostrel-gold"
            />
            Mailbox access
          </label>
          <p className="ml-5 text-[11px] text-ostrel-ink-muted">mailbox doors open for this person</p>
        </div>
        {save.isError && <p className="text-xs text-ostrel-danger">{save.error.message}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : person ? 'Save changes' : 'Add person'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function DirectoryPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const locationUid = params.get('location') ?? undefined
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const canOperate = useCanOperate()

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const persons = usePersons(q, locationUid)
  const credentials = useCredentials()
  const groups = useGroups()
  const tree = useLocationTree()
  const unknownCreds = useUnknownCredentials('open')
  const locationPathsMap = useMemo(
    () => (tree.data ? locationPaths(tree.data) : new Map<string, string>()),
    [tree.data],
  )
  const locationName = locationUid ? (locationPathsMap.get(locationUid) ?? locationUid) : undefined

  const groupNames = new Map((groups.data ?? []).map((g) => [g.unique_id, g.name]))

  const credCounts = new Map<string, number>()
  for (const c of credentials.data ?? []) {
    if (c.person) credCounts.set(c.person, (credCounts.get(c.person) ?? 0) + 1)
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Directory"
        title="People"
        action={
          <>
            {canOperate && (
              <Button variant="ghost" onClick={() => navigate('/directory/duplicates')}>
                Merge duplicates
              </Button>
            )}
            <Button onClick={() => setAddOpen(true)}>
              + Add person
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search people"
            />
          </div>
          <span className="tnum shrink-0 text-[11px] text-ostrel-ink-muted">
            {persons.data ? `${persons.data.length} ${persons.data.length === 1 ? 'person' : 'people'}` : ''}
          </span>
        </div>
        {locationName && (
          <div className="flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
            <span>Filtered by location:</span>
            <Chip tone="info" label={locationName} />
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params)
                next.delete('location')
                setParams(next, { replace: true })
              }}
              className="text-ostrel-gold hover:text-ostrel-accent"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {(unknownCreds.data ?? []).length > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-sm border border-ostrel-gold/30 bg-ostrel-gold/10 p-3">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-ostrel-gold" />
            <span className="text-sm text-ostrel-ink">
              <span className="tnum font-semibold">{unknownCreds.data!.length}</span> pending unknown credential
              {unknownCreds.data!.length === 1 ? '' : 's'} from connected systems
            </span>
          </div>
          <Link
            to="/unknown-credentials"
            className="text-[11px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
          >
            Review →
          </Link>
        </div>
      )}

      {persons.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : persons.isError ? (
        <EmptyState title="Could not load people" hint={persons.error.message} />
      ) : persons.data.length === 0 ? (
        <EmptyState
          title={
            q
              ? `No people match “${q}”`
              : locationName
                ? `No people at “${locationName}”`
                : 'No people yet'
          }
          hint={
            q
              ? 'Try a different search.'
              : locationName
                ? 'No active occupancies at this location or below it.'
                : 'Add the first person to the directory.'
          }
          action={
            !q && !locationName ? (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                + Add person
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Person</TH>
              <TH>Contact</TH>
              <TH>Status</TH>
              <TH>Credentials</TH>
              <TH className="w-8" />
            </tr>
          </THead>
          <TBody>
            {persons.data.map((p) => {
              const validity = validityWindow(p.valid_from, p.valid_to)
              return (
                <TR
                  key={p.unique_id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/directory/${p.unique_id}`)}
                >
                  <TD>
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-ostrel-line bg-ostrel-sand text-[11px] font-semibold text-ostrel-ink">
                        {initials(p.display_name || '?')}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ostrel-ink">
                          {p.display_name || 'Unnamed'}
                        </span>
                        <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                          {p.emails[0] ?? p.unique_id}
                          {p.groups.length > 0 &&
                            ` · ${p.groups.map((g) => groupNames.get(g) ?? g).join(', ')}`}
                          {(p.locations?.length ?? 0) > 0 && ` · ${p.locations?.join(', ')}`}
                        </span>
                      </span>
                    </div>
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted">{p.phones[0] ?? '—'}</span>
                  </TD>
                  <TD>
                    <div className="flex flex-col items-start gap-1">
                      <Chip tone={personStatusTone(p.status)} label={p.status} />
                      {validity && (
                        <span className="tnum text-[10px] text-ostrel-ink-muted">{validity}</span>
                      )}
                    </div>
                  </TD>
                  <TD>
                    <span className="tnum text-sm text-ostrel-ink">{credCounts.get(p.unique_id) ?? 0}</span>
                  </TD>
                  <TD className="text-right text-ostrel-ink-muted">→</TD>
                </TR>
              )
            })}
          </TBody>
        </Table>
      )}

      <PersonFormModal open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  )
}
